import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import StarIcon from "@material-ui/icons/Star";
import CloseIcon from "@material-ui/icons/Close";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import { connect } from "react-redux";
import {
  uploadHomeCoverImage,
  deleteHomeImageFile,
  editHomeImages,
} from "src/actions/HomeActions";
import { toast } from "react-toastify";

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
);

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
  divider: "#F1F5F9",
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: TOKENS.surface,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
  },
  backButton: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.textSecondary,
    "&:hover": {
      backgroundColor: TOKENS.divider,
      color: TOKENS.textPrimary,
    },
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: TOKENS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 14,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "20px 24px 0 24px",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  sectionContent: {
    padding: "16px 24px 24px 24px",
  },
  pondWrapper: {
    "& .filepond--root": { fontFamily: "inherit" },
    "& .filepond--panel-root": {
      backgroundColor: "#FAFBFC",
      borderRadius: 12,
      border: `2px dashed ${TOKENS.border}`,
    },
    "& .filepond--drop-label": {
      color: TOKENS.textSecondary,
      fontSize: 14,
    },
    "& .filepond--label-action": {
      color: TOKENS.accent,
      fontWeight: 600,
      textDecorationColor: TOKENS.accent,
    },
    "& .filepond--item-panel": { borderRadius: 10 },
    "& .filepond--image-preview-wrapper": { borderRadius: 10 },
  },
  hint: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 8,
  },

  /* ── Gallery grid ─────────────────────────────────────────────── */
  gallerySection: {
    padding: "0 24px 24px 24px",
  },
  galleryLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: TOKENS.textSecondary,
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  galleryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
    gap: 10,
  },
  imageCard: {
    position: "relative",
    borderRadius: 10,
    overflow: "hidden",
    cursor: "pointer",
    aspectRatio: "4 / 3",
    border: "2px solid transparent",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: "#A5B4FC",
    },
  },
  imageCardCover: {
    borderColor: `${TOKENS.accent} !important`,
    boxShadow: `0 0 0 2px ${TOKENS.accent}33`,
  },
  imageThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  imageOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.35) 100%)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    padding: 6,
    opacity: 0,
    transition: "opacity 0.15s ease",
    "$imageCard:hover &": {
      opacity: 1,
    },
  },
  imageOverlayVisible: {
    opacity: "1 !important",
  },
  coverBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: 700,
    padding: "3px 8px",
    borderRadius: 6,
    letterSpacing: "0.02em",
  },
  setCoverHint: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    backgroundColor: "rgba(255,255,255,0.85)",
    color: TOKENS.textPrimary,
    fontSize: 10,
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: 6,
    backdropFilter: "blur(4px)",
  },
  removeBtn: {
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: 6,
    backgroundColor: "rgba(0,0,0,0.45)",
    color: "#FFFFFF",
    padding: 0,
    "&:hover": {
      backgroundColor: "#EF4444",
    },
  },

  /* ── Footer ───────────────────────────────────────────────────── */
  counterBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    borderTop: `1px solid ${TOKENS.border}`,
  },
  counterText: {
    fontSize: 13,
    color: TOKENS.textSecondary,
  },
  counterHighlight: {
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 24px",
    borderTop: `1px solid ${TOKENS.border}`,
  },
  cancelButton: {
    color: TOKENS.textSecondary,
    borderColor: TOKENS.border,
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",
    "&:hover": {
      borderColor: "#94A3B8",
      backgroundColor: "#F8FAFC",
    },
  },
  saveButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 24px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
    "&:disabled": { backgroundColor: "#C7D2FE", color: "#FFFFFF" },
  },
}));

const UploadHomeImages = ({
  uploadHomeCoverImage,
  deleteHomeImageFile,
  editHomeImages,
  home,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);

  const handleRemoveImage = (index) => {
    setUploadedImages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // If we removed the current cover, reset to first image
      if (index === coverIndex) {
        setCoverIndex(0);
      } else if (index < coverIndex) {
        setCoverIndex((c) => c - 1);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (uploadedImages.length < 3) {
      toast.error("Please upload at least 3 images");
      return;
    }

    const coverImage = uploadedImages[coverIndex];
    const otherImages = uploadedImages.filter((_, i) => i !== coverIndex);

    const obj = {
      ...home,
      coverImage_URL: coverImage,
      otherImages: otherImages.map((image_url) => ({ image_url })),
    };
    await editHomeImages(obj, home.id, navigate);
  };

  return (
    <Container className={classes.root}>
      {/* Page Header */}
      <Box className={classes.pageHeader}>
        <Button className={classes.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon style={{ fontSize: 18 }} />
        </Button>
        <Box>
          <Typography className={classes.pageTitle}>Upload Images</Typography>
          <Typography className={classes.pageSubtitle}>
            Add photos of your home — tap any image to set it as the cover
          </Typography>
        </Box>
      </Box>

      <Card className={classes.card}>
        {/* Upload section */}
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>Home Images</Typography>
          <Typography className={classes.sectionSubtitle}>
            Upload up to 10 high-quality images. Minimum 3 required.
          </Typography>
        </Box>

        <CardContent className={classes.sectionContent}>
          <Box className={classes.pondWrapper}>
            <FilePond
              files={files}
              onupdatefiles={setFiles}
              allowMultiple={true}
              maxFiles={10}
              allowImageResize={true}
              imageResizeTargetWidth={1200}
              imageResizeTargetHeight={800}
              imageResizeMode="cover"
              allowRemove={true}
              acceptedFileTypes={["image/*"]}
              maxFileSize="5MB"
              server={{
                process: async (
                  fieldName,
                  file,
                  metadata,
                  load,
                  error,
                  progress,
                  abort,
                ) => {
                  try {
                    const formData = new FormData();
                    formData.append("file", file);
                    const res = await uploadHomeCoverImage(formData);
                    setUploadedImages((prev) => [...prev, res]);
                    load();
                  } catch (err) {
                    error("Upload failed");
                  }
                  return { abort: () => abort() };
                },
                revert: async (uniqueFileId, load) => {
                  try {
                    load();
                  } catch (err) {
                    toast.error("Failed to remove image.");
                    load();
                  }
                },
              }}
              labelIdle='Drag & drop images or <span class="filepond--label-action">Browse</span>'
            />
          </Box>
          <Typography className={classes.hint}>
            Accepted formats: JPG, PNG, WebP. Max 5 MB per file.
          </Typography>
        </CardContent>

        {/* Gallery — select cover */}
        {uploadedImages.length > 0 && (
          <Box className={classes.gallerySection}>
            <Typography className={classes.galleryLabel}>
              Tap an image to set as cover — {uploadedImages.length} uploaded
            </Typography>
            <Box className={classes.galleryGrid}>
              {uploadedImages.map((imgUrl, index) => {
                const isCover = index === coverIndex;
                return (
                  <Box
                    key={index}
                    className={`${classes.imageCard} ${
                      isCover ? classes.imageCardCover : ""
                    }`}
                    onClick={() => setCoverIndex(index)}
                  >
                    <img
                      src={imgUrl}
                      alt={`Upload ${index + 1}`}
                      className={classes.imageThumb}
                    />
                    <Box
                      className={`${classes.imageOverlay} ${
                        isCover ? classes.imageOverlayVisible : ""
                      }`}
                    >
                      {isCover ? (
                        <span className={classes.coverBadge}>
                          <StarIcon style={{ fontSize: 12 }} />
                          Cover
                        </span>
                      ) : (
                        <span className={classes.setCoverHint}>
                          Set as cover
                        </span>
                      )}
                      <Button
                        className={classes.removeBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(index);
                        }}
                        size="small"
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Counter bar */}
        <Box className={classes.counterBar}>
          <Typography className={classes.counterText}>
            <span className={classes.counterHighlight}>
              {uploadedImages.length}
            </span>{" "}
            of 10 images uploaded
            {uploadedImages.length > 0 && uploadedImages.length < 3 && (
              <span> — need at least {3 - uploadedImages.length} more</span>
            )}
          </Typography>
        </Box>

        {/* Footer */}
        <Box className={classes.footerActions}>
          <Button
            variant="outlined"
            className={classes.cancelButton}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className={classes.saveButton}
            onClick={handleSubmit}
            disabled={uploadedImages.length < 3}
          >
            Save Images
          </Button>
        </Box>
      </Card>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  home: state.homes.home,
});

export default connect(mapStateToProps, {
  uploadHomeCoverImage,
  deleteHomeImageFile,
  editHomeImages,
})(UploadHomeImages);
